import { MenuFoldOutlined, MenuUnfoldOutlined, SearchOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import { Button, Input, Layout, Menu, Tooltip, Typography, type InputRef, type MenuProps } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { Mark, Wordmark } from "./Logo";
import { HOME, NAV, PAGES, isPage, navFor, trailOf, type NavItem } from "./nav";

type MenuItems = NonNullable<MenuProps["items"]>;

const COLLAPSED_KEY = "zerpid.nav.collapsed";
const FAV = "fav:";

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function save(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage blocked: the choice lasts until reload
  }
}

/**
 * The sidebar, ERP style: sections (Penjualan · OMS, Gudang · WMS, Pembelian,
 * Keuangan, Sistem) over pages and groups of pages. It stays in view while
 * the page scrolls, folds down to icons, finds a page by name (Ctrl+K), and
 * keeps the pages someone starred at the top. Folding and stars are this
 * browser's, per user.
 */
export function Sidebar() {
  const { t } = useI18n();
  const { can, me } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const favKey = `zerpid.nav.fav.${me?.user.id ?? ""}`;
  const [collapsed, setCollapsed] = useState(() => load(COLLAPSED_KEY, false));
  const [favs, setFavs] = useState<string[]>(() => load<string[]>(favKey, []));
  const [query, setQuery] = useState("");
  const search = useRef<InputRef>(null);
  const menuBox = useRef<HTMLDivElement>(null);

  const selected = navFor(location.pathname)?.key ?? HOME;
  // Groups open as the person left them, plus the way to the page on screen.
  const [open, setOpen] = useState<string[]>(() => trailOf(selected));
  const [seen, setSeen] = useState(selected);
  if (seen !== selected) {
    setSeen(selected);
    const need = trailOf(selected).filter((k) => !open.includes(k));
    if (need.length > 0) setOpen([...open, ...need]);
  }

  const fold = useCallback((v: boolean) => {
    setCollapsed(v);
    save(COLLAPSED_KEY, v);
  }, []);

  const toggleFav = useCallback(
    (key: string) =>
      setFavs((prev) => {
        const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
        save(favKey, next);
        return next;
      }),
    [favKey],
  );

  // Ctrl+K (or ⌘K) finds a page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        fold(false);
        requestAnimationFrame(() => search.current?.focus());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fold]);

  // The page on screen stays visible in a long menu.
  useEffect(() => {
    menuBox.current?.querySelector(".ant-menu-item-selected")?.scrollIntoView({ block: "nearest" });
  }, [selected, collapsed]);

  const allowed = useCallback((n: NavItem) => can(...n.perms), [can]);

  const label = useCallback(
    (n: NavItem, starrable: boolean) => {
      if (!starrable) return t(n.label);
      const on = favs.includes(n.key);
      return (
        <span className="z-nav-label">
          <span className="z-nav-text">{t(n.label)}</span>
          <span
            role="button"
            tabIndex={-1}
            aria-label={t(on ? "nav.unfavorite" : "nav.favorite")}
            title={t(on ? "nav.unfavorite" : "nav.favorite")}
            className={on ? "z-nav-star z-nav-star-on" : "z-nav-star"}
            onClick={(e) => {
              e.stopPropagation();
              toggleFav(n.key);
            }}
          >
            {on ? <StarFilled /> : <StarOutlined />}
          </span>
        </span>
      );
    },
    [favs, t, toggleFav],
  );

  const items = useMemo<MenuItems>(() => {
    const build = (list: NavItem[]): MenuItems =>
      list.filter(allowed).flatMap((n): MenuItems => {
        if (n.section) {
          const children = build(n.children ?? []);
          if (children.length === 0) return [];
          // Folded, a section is a rule between icons.
          return collapsed ? [{ type: "divider" as const, key: n.key }, ...children] : [{ type: "group" as const, key: n.key, label: t(n.label), children }];
        }
        if (n.children) {
          const children = build(n.children);
          return children.length === 0 ? [] : [{ key: n.key, icon: n.icon, label: t(n.label), children }];
        }
        return [{ key: n.key, icon: n.icon, label: label(n, !collapsed && n.key !== HOME) }];
      });
    const starred = favs.map((k) => PAGES.find((p) => p.item.key === k)).filter((p) => p !== undefined && allowed(p.item));
    const favItems: MenuItems =
      starred.length === 0 || collapsed
        ? []
        : [
            {
              type: "group",
              key: "sec:favorites",
              label: t("nav.favorites"),
              children: starred.map((p) => ({ key: FAV + p!.item.key, icon: p!.item.icon ?? p!.trail.at(-1)?.icon, label: label(p!.item, true) })),
            },
          ];
    return [...favItems, ...build(NAV)];
  }, [allowed, collapsed, favs, label, t]);

  // Searching: the matching pages, flat, with where they sit.
  const q = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      q === ""
        ? []
        : PAGES.filter((p) => allowed(p.item) && p.trail.every(allowed)).filter((p) =>
            [p.item, ...p.trail].some((n) => t(n.label).toLowerCase().includes(q)),
          ),
    [allowed, q, t],
  );
  const results: MenuItems = matches.map((p) => ({
    key: p.item.key,
    icon: p.item.icon ?? p.trail.at(-1)?.icon,
    label: (
      <span className="z-nav-result">
        <span className="z-nav-text">{t(p.item.label)}</span>
        <Typography.Text type="secondary" className="z-nav-where">
          {p.trail.map((n) => t(n.label)).join(" › ")}
        </Typography.Text>
      </span>
    ),
  }));

  const go = (key: string) => {
    const route = key.startsWith(FAV) ? key.slice(FAV.length) : key;
    if (!isPage(route)) return;
    navigate(route);
    if (query) setQuery("");
  };

  return (
    <Layout.Sider
      width={256}
      theme="light"
      className="z-sider"
      collapsed={collapsed}
      collapsedWidth={64}
      trigger={null}
      breakpoint="lg"
      onBreakpoint={(narrow) => narrow && setCollapsed(true)}
    >
      <div className="z-sider-head">
        {collapsed ? (
          <Button type="text" aria-label="Zerpid" onClick={() => navigate(HOME)} style={{ padding: 4, height: "auto" }}>
            <Mark size={28} />
          </Button>
        ) : (
          <Wordmark size={26} />
        )}
      </div>
      <div className="z-sider-search">
        {collapsed ? (
          <Tooltip title={`${t("nav.search")} (Ctrl+K)`} placement="right">
            <Button
              type="text"
              icon={<SearchOutlined />}
              aria-label={t("nav.search")}
              block
              onClick={() => {
                fold(false);
                requestAnimationFrame(() => search.current?.focus());
              }}
            />
          </Tooltip>
        ) : (
          <Input
            ref={search}
            allowClear
            size="middle"
            prefix={<SearchOutlined style={{ color: "var(--z-muted)" }} />}
            suffix={query ? undefined : <span className="z-kbd">Ctrl K</span>}
            placeholder={t("nav.search")}
            aria-label={t("nav.search")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && matches[0]) go(matches[0].item.key);
              if (e.key === "Escape") setQuery("");
            }}
          />
        )}
      </div>
      <div className="z-sider-menu" ref={menuBox}>
        {q !== "" ? (
          results.length > 0 ? (
            <Menu mode="inline" items={results} selectedKeys={[selected]} onClick={({ key }) => go(key)} className="z-nav" />
          ) : (
            <Typography.Paragraph type="secondary" style={{ padding: "8px 24px" }}>
              {t("nav.noMatch")}
            </Typography.Paragraph>
          )
        ) : (
          <Menu
            mode="inline"
            items={items}
            inlineCollapsed={collapsed}
            selectedKeys={[selected, FAV + selected]}
            openKeys={collapsed ? undefined : open}
            onOpenChange={(keys) => setOpen(keys)}
            onClick={({ key }) => go(key)}
            className="z-nav"
          />
        )}
      </div>
      <div className="z-sider-foot">
        <Tooltip title={collapsed ? t("nav.expand") : undefined} placement="right">
          <Button
            type="text"
            block
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => fold(!collapsed)}
            aria-label={t(collapsed ? "nav.expand" : "nav.collapse")}
            style={{ justifyContent: collapsed ? "center" : "flex-start" }}
          >
            {collapsed ? null : t("nav.collapse")}
          </Button>
        </Tooltip>
      </div>
    </Layout.Sider>
  );
}
